/**
 * MobileContractsList
 *
 * Mobile card-based replacement for ContractsDataTable.
 * Shows each contract/collab as a touch-friendly card with key info.
 * Supports search, pagination, and tap-to-view details.
 */
import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Pagination,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Langs, { Localize } from "../../../app/lang/langs";
import { getTagClass } from "../../../app/utils/helpers";
import { formatDate } from "../contractsTableUtils";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const EASE_OUT = "cubic-bezier(0, 0, 0.2, 1)";
const PAGE_SIZE = 20;

/* ------------------------------------------------------------------ */
/*  Premium style tokens                                               */
/* ------------------------------------------------------------------ */
const GLASS_BG = "rgba(255,255,255,0.72)";
const GLASS_BG_CARD = "rgba(255,255,255,0.82)";
const GLASS_BORDER = "1px solid rgba(255,255,255,0.45)";
const GLASS_BORDER_SUBTLE = "1px solid rgba(0,0,0,0.05)";
const SHADOW_CARD =
  "0 2px 12px rgba(0,40,128,0.06), 0 1px 4px rgba(0,0,0,0.04)";
const SHADOW_CARD_ACTIVE =
  "0 1px 4px rgba(0,40,128,0.04)";
const SHADOW_SEARCH_FOCUS = "0 0 0 3px rgba(255,144,21,0.18)";
const SHADOW_CHIP = "0 1px 4px rgba(0,40,128,0.08)";
const SHADOW_AVATAR_INTERNE =
  "0 2px 8px rgba(255,144,21,0.35), 0 0 0 2px rgba(255,255,255,0.8)";
const SHADOW_AVATAR_EXTERNE =
  "0 2px 8px rgba(251,105,98,0.35), 0 0 0 2px rgba(255,255,255,0.8)";
const SHADOW_PAGINATION_SELECTED = "0 2px 10px rgba(255,144,21,0.4)";
const BADGE_BG = "rgba(255,144,21,0.1)";
const BADGE_COLOR = "rgb(255,144,21)";

const severityToColor = {
  success: "success",
  info: "info",
  warning: "warning",
  danger: "error",
  null: "default",
  undefined: "default",
};

/**
 * @param {Object} props
 * @param {Array}  props.data - allCollabsList (contracts view data)
 * @param {Function} props.onCardTap - called with collab data when tapped
 * @param {Function} props.onValueChange - called with filtered data for export
 */
export default function MobileContractsList({
  data,
  onCardTap,
  onValueChange,
}) {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);

  const baseData = data || [];

  // Filter by search text across key fields
  const filteredData = useMemo(() => {
    if (!searchText.trim()) return baseData;
    const terms = searchText.toLowerCase().split(" ").filter(Boolean);
    return baseData.filter((item) => {
      const searchStr = [
        item.nom,
        item.prenom,
        item.company,
        item.type_collab,
        item.statut,
        item.type_contrat,
        item.nature_emploi,
        item.etablissement,
        item.motif_sortie,
        item.subcontractor,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.every((t) => searchStr.includes(t));
    });
  }, [baseData, searchText]);

  // Notify parent of filtered data for export
  const notifyParent = useCallback(() => {
    if (onValueChange) onValueChange(filteredData);
  }, [filteredData, onValueChange]);

  useEffect(() => {
    notifyParent();
  }, [notifyParent]);

  // Reset page on search change
  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const getStatusColor = (statut) => {
    if (["Présent", "Actif"].includes(statut)) return "success";
    if (statut === "Parti") return "error";
    return "default";
  };

  const getTypeChipColor = (type) => {
    const severity = getTagClass(type);
    return severityToColor[severity] || "default";
  };

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2.5,
        mt: "130px",
        height: "calc(100vh - 130px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search bar — glassmorphic */}
      <TextField
        fullWidth
        size="small"
        placeholder={Localize("Search") + "..."}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                fontSize="small"
                sx={{
                  color: "rgba(0,40,128,0.4)",
                  transition: TRANSITION_SMOOTH,
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,
          "& input": {
            fontSize: "16px",
            fontWeight: 400,
            letterSpacing: "0.01em",
            "&::placeholder": { opacity: 0.45 },
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            bgcolor: GLASS_BG,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: GLASS_BORDER,
            transition: TRANSITION_SMOOTH,
            boxShadow: "0 2px 8px rgba(0,40,128,0.04)",
            "& fieldset": {
              borderColor: "transparent",
              transition: TRANSITION_SMOOTH,
            },
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.82)",
              boxShadow: "0 2px 12px rgba(0,40,128,0.07)",
              "& fieldset": { borderColor: "rgba(0,40,128,0.1)" },
            },
            "&.Mui-focused": {
              bgcolor: "rgba(255,255,255,0.9)",
              boxShadow: SHADOW_SEARCH_FOCUS,
              "& fieldset": {
                borderColor: "rgb(255,144,21) !important",
                borderWidth: "1.5px !important",
              },
              "& .MuiInputAdornment-root svg": {
                color: "rgb(255,144,21)",
              },
            },
          },
        }}
      />

      {/* Results count — pill badge */}
      <Box sx={{ mb: 1.25, px: 0.25 }}>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1.25,
            py: 0.35,
            borderRadius: "10px",
            bgcolor: BADGE_BG,
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: "0.72rem",
              letterSpacing: "0.03em",
              color: BADGE_COLOR,
              lineHeight: 1.5,
            }}
          >
            {filteredData.length}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              fontSize: "0.72rem",
              letterSpacing: "0.02em",
              color: "rgba(0,40,128,0.55)",
              lineHeight: 1.5,
            }}
          >
            {Localize("results")}
          </Typography>
        </Box>
      </Box>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,40,128,0.1)",
            borderRadius: 3,
            "&:hover": { background: "rgba(0,40,128,0.18)" },
          },
        }}
      >
        {paginatedData.map((item, index) => (
          <Fade
            in
            key={item.id}
            timeout={300 + index * 50}
            easing={{ enter: EASE_OUT }}
          >
            <Card
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: "18px",
                overflow: "hidden",
                bgcolor: GLASS_BG_CARD,
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: GLASS_BORDER,
                boxShadow: SHADOW_CARD,
                transition: `transform 280ms ${EASE_OUT}, box-shadow 280ms ${EASE_OUT}, background-color 280ms ${EASE_OUT}`,
                "&:active": {
                  transform: "scale(0.975)",
                  boxShadow: SHADOW_CARD_ACTIVE,
                  bgcolor: "rgba(255,255,255,0.92)",
                },
              }}
            >
              <CardActionArea
                onClick={() => onCardTap(item)}
                sx={{
                  p: 0,
                  "& .MuiCardActionArea-focusHighlight": {
                    background: "linear-gradient(135deg, rgba(255,144,21,0.04), rgba(0,40,128,0.03))",
                  },
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  {/* Top row: name + status */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        minWidth: 0,
                      }}
                    >
                      {/* Avatar circle with glow */}
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background:
                            item.type_collab === "externe"
                              ? "linear-gradient(135deg, rgb(251,105,98), rgba(251,105,98,0.75))"
                              : "linear-gradient(135deg, rgb(255,144,21), rgba(255,193,0,0.85))",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "0.82rem",
                          letterSpacing: "0.02em",
                          flexShrink: 0,
                          boxShadow:
                            item.type_collab === "externe"
                              ? SHADOW_AVATAR_EXTERNE
                              : SHADOW_AVATAR_INTERNE,
                          transition: TRANSITION_SMOOTH,
                        }}
                      >
                        {(item.prenom?.[0] || "").toUpperCase()}
                        {(item.nom?.[0] || "").toUpperCase()}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            lineHeight: 1.3,
                            letterSpacing: "-0.015em",
                            color: "rgba(0,40,128,0.9)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.prenom} {item.nom}
                        </Typography>
                        {item.company && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.73rem",
                              fontWeight: 450,
                              color: "rgba(0,40,128,0.45)",
                              letterSpacing: "0.01em",
                              lineHeight: 1.4,
                            }}
                          >
                            {item.company}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Status chip — refined */}
                    {item.is_new ? (
                      <Chip
                        label={<Langs str="New" />}
                        size="small"
                        color="info"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.68rem",
                          height: 24,
                          borderRadius: "8px",
                          letterSpacing: "0.03em",
                          boxShadow: "0 1px 6px rgba(25,118,210,0.25)",
                          transition: TRANSITION_SMOOTH,
                        }}
                      />
                    ) : item.statut ? (
                      <Chip
                        label={item.statut}
                        size="small"
                        color={getStatusColor(item.statut)}
                        variant={
                          ["Présent", "Actif"].includes(item.statut)
                            ? "filled"
                            : "outlined"
                        }
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.68rem",
                          height: 24,
                          borderRadius: "8px",
                          letterSpacing: "0.02em",
                          boxShadow:
                            ["Présent", "Actif"].includes(item.statut)
                              ? "0 1px 6px rgba(58,156,96,0.25)"
                              : item.statut === "Parti"
                              ? "0 1px 6px rgba(251,105,98,0.2)"
                              : "none",
                          transition: TRANSITION_SMOOTH,
                        }}
                      />
                    ) : null}
                  </Box>

                  {/* Info chips — refined with soft shadows */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.6,
                      mt: 0.25,
                    }}
                  >
                    {item.type_collab && (
                      <Chip
                        label={Localize(item.type_collab)}
                        size="small"
                        color={getTypeChipColor(item.type_collab)}
                        variant="outlined"
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 500,
                          height: 24,
                          borderRadius: "8px",
                          bgcolor: "rgba(255,255,255,0.6)",
                          backdropFilter: "blur(6px)",
                          boxShadow: SHADOW_CHIP,
                          transition: TRANSITION_SMOOTH,
                        }}
                      />
                    )}
                    {item.type_contrat && (
                      <Chip
                        label={item.type_contrat}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 500,
                          height: 24,
                          borderRadius: "8px",
                          bgcolor: "rgba(255,255,255,0.6)",
                          backdropFilter: "blur(6px)",
                          boxShadow: SHADOW_CHIP,
                          borderColor: "rgba(0,40,128,0.12)",
                          color: "rgba(0,40,128,0.7)",
                          transition: TRANSITION_SMOOTH,
                        }}
                      />
                    )}
                    {item.nature_emploi && (
                      <Chip
                        label={item.nature_emploi}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 500,
                          height: 24,
                          maxWidth: 170,
                          borderRadius: "8px",
                          bgcolor: "rgba(255,255,255,0.6)",
                          backdropFilter: "blur(6px)",
                          boxShadow: SHADOW_CHIP,
                          borderColor: "rgba(0,40,128,0.12)",
                          color: "rgba(0,40,128,0.7)",
                          transition: TRANSITION_SMOOTH,
                        }}
                      />
                    )}
                  </Box>

                  {/* Contract details row — refined */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.5,
                      mt: 1.25,
                      alignItems: "center",
                    }}
                  >
                    {item.exitDate && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 450,
                          color: "rgba(0,40,128,0.5)",
                          letterSpacing: "0.01em",
                          lineHeight: 1.4,
                        }}
                      >
                        <Langs str="Date de fin de du contrat en cours" />:{" "}
                        <Box
                          component="strong"
                          sx={{
                            fontWeight: 600,
                            color: "rgba(0,40,128,0.7)",
                          }}
                        >
                          {formatDate(item.exitDate)}
                        </Box>
                      </Typography>
                    )}
                    {item.tjm_achat && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 450,
                          color: "rgba(0,40,128,0.5)",
                          letterSpacing: "0.01em",
                          lineHeight: 1.4,
                        }}
                      >
                        TJM:{" "}
                        <Box
                          component="strong"
                          sx={{
                            fontWeight: 600,
                            color: "rgba(0,40,128,0.7)",
                          }}
                        >
                          {item.tjm_achat}
                        </Box>
                        {item.tjm_vente ? ` / ${item.tjm_vente}` : ""}
                      </Typography>
                    )}
                  </Box>

                  {item.etablissement && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 450,
                        mt: 0.75,
                        display: "block",
                        color: "rgba(0,40,128,0.4)",
                        letterSpacing: "0.01em",
                        lineHeight: 1.4,
                      }}
                    >
                      {item.etablissement}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Fade>
        ))}

        {paginatedData.length === 0 && (
          <Fade in timeout={500}>
            <Box
              sx={{
                textAlign: "center",
                py: 10,
                opacity: 0.7,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 450,
                  color: "rgba(0,40,128,0.35)",
                  letterSpacing: "0.02em",
                }}
              >
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
